import { Component, createRef, ReactNode } from 'react';

import { ReactPageClick } from 'react-page-click';
import {
  FormattedMessage,
  injectIntl,
  IntlShape,
  MessageDescriptor,
  PrimitiveType,
} from 'react-intl';
import { isEmpty, isString, get, isNil, range } from 'lodash';

import { Menu } from 'components/navigation/menu/menu';
import { getDropdownOptionsSettings } from 'components/utils/dropdown';

import * as styled from './styles/dropdown';

import i18n from './utils/i18n';

export type DropdownOption = {
  slug: string | null;
  linkName?: string | I18nMessage | ReactNode;
  textValue?: string;
  values?: Record<string, PrimitiveType>;
  disabled?: boolean;
  fn: (
    e: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
  ) => void;
};

type DropdownComponentProps = {
  className: string;
  optionsPosition?: 'top' | 'bottom';
  options: DropdownOption[];
  padding: string;
  size: 'small' | 'default';
  onSelect: (optionItemSlug: string | null) => void;
  onFocus: (focused: boolean) => void;
  intl: IntlShape;
  typeaheadPlaceholder?: string;
  optionsCount?: I18nMessage;
  children: React.ReactNode | React.ReactNode[];
  typeahead?: boolean;
  onTypeaheadChange: (value: string) => void;
  selectTag: string;
  disabled: boolean;
  onOptionsPosition: (position: string) => void;
  optionsHeight: number;
  optionsFixed: boolean;
  displayClear: boolean;
  dataManual: string;
  active: boolean;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  keepOpenOnSelect?: boolean;
};

type SelectedTagProps = {
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  onFocus: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  onKeyUp: (e: React.KeyboardEvent<HTMLElement>) => void;
  padding: string | null;
};

type State = {
  activeMenu: boolean;
  focused: boolean;
  typeaheadValue: string;
  selectedHighlighted: number | null;
  hidden: boolean;
  options: DropdownOption[];
};

// menu max div height
const MAX_OPTIONS_SIZE = 25;

function getTypeaheadHeight(isEnabled: boolean, size: string) {
  if (!isEnabled) return 0;
  if (size === 'small') return 3.5;
  return 4.5;
}

class DropdownComponent extends Component<DropdownComponentProps, State> {
  static defaultProps = {
    size: 'default' as 'small' | 'default',
    options: [],
    optionsPosition: 'bottom' as 'top' | 'bottom' | undefined,
    selectTag: 'link',
    disabled: false,
    optionsHeight: 5,
    optionsFixed: false,
  };

  constructor(props: DropdownComponentProps) {
    super(props);

    this.state = {
      activeMenu: false,
      focused: false,
      typeaheadValue: '',
      selectedHighlighted: null,
      hidden: true,
      options: props.options,
    };
  }

  componentDidMount() {
    const { wrapperRef, optionsFixed } = this.props;

    if (optionsFixed) {
      window.addEventListener('resize', this.recalculateOptionsPosition);
    }

    if (wrapperRef?.current && optionsFixed) {
      this.setupFixedOptionsCallbacks();
    }
  }

  componentDidUpdate() {
    const { wrapperRef, optionsFixed } = this.props;
    const { activeMenu, hidden } = this.state;
    if (this.typeaheadInput && activeMenu) {
      this.typeaheadInput.focus();
    }

    if (activeMenu && hidden) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hidden: false });
    }

    if (wrapperRef?.current && optionsFixed) {
      this.setupFixedOptionsCallbacks();
    }
  }

  componentWillUnmount() {
    const { wrapperRef } = this.props;
    window.removeEventListener('resize', this.recalculateOptionsPosition);
    wrapperRef?.current?.removeEventListener(
      'scroll',
      this.recalculateOptionsPosition
    );
  }

  handleTypeaheadChange(value: string) {
    this.setState({ typeaheadValue: value, selectedHighlighted: null }, () => {
      const { options: unfilteredOptions } = this.props;
      if (!value) {
        this.setState({ options: unfilteredOptions });
      } else {
        this.setState({
          options: this.filterOptions(unfilteredOptions, value),
        });
      }
    });
  }

  handleKeyUp(e: React.KeyboardEvent<HTMLElement>) {
    const options = this.optionMenuItems().links;
    const { selectedHighlighted } = this.state;

    switch (e.keyCode) {
      case 13: {
        if (
          selectedHighlighted != null &&
          !options[selectedHighlighted].disabled
        ) {
          options[selectedHighlighted].fn!(e);
        }
        break;
      }
      case 38:
        this.highlightEnabledOption(options, -1);
        break;
      case 40:
        this.highlightEnabledOption(options, 1);
        break;
      default:
        break;
    }
  }

  handleKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    const {
      nativeEvent: { keyCode },
    } = e;

    if (keyCode === 38 || keyCode === 40) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  handleKeyPress(e: React.KeyboardEvent<HTMLElement>) {
    if (e.nativeEvent.keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  handleClearSelection = (e: React.MouseEvent<HTMLElement>) => {
    this.optionSelected(e, { slug: null, fn: () => {} });
  };

  getFilterText(option: DropdownOption, intl: IntlShape) {
    const label = get(option, 'label');
    if (!isNil(label)) {
      return label;
    }

    const textValue = get(option, 'textValue');
    if (!isNil(textValue)) {
      return textValue;
    }

    if (get(option, 'linkName.id')) {
      const formattedMessage = intl.formatMessage(
        option.linkName as MessageDescriptor,
        option.values
      );
      return formattedMessage;
    }

    return isString(option?.linkName) ? option.linkName : '';
  }

  setupFixedOptionsCallbacks() {
    const { wrapperRef } = this.props;

    wrapperRef?.current?.addEventListener(
      'scroll',
      this.recalculateOptionsPosition
    );
  }

  highlightEnabledOption(options: DropdownOption[], step: 1 | -1) {
    const { selectedHighlighted } = this.state;
    const start = selectedHighlighted ?? (step === 1 ? -1 : options.length);
    const index = range(1, options.length + 1)
      .map(
        (offset) => (start + step * offset + options.length) % options.length
      )
      .find((candidate) => !options[candidate].disabled);

    if (index === undefined) return;

    this.setState({ selectedHighlighted: index });
    this.optionsMenu?.scrollTo(index);
  }

  filterOptions(options: DropdownOption[], value: string) {
    const { intl } = this.props;
    const normalizedValue = value.toLowerCase();

    return options.filter((option) => {
      const filterText = this.getFilterText(option, intl);
      return filterText.toLowerCase().includes(normalizedValue);
    });
  }

  optionsMenu?: Menu | null;

  typeaheadInput?: HTMLInputElement | null;

  selectRef?: React.RefObject<HTMLDivElement> = createRef();

  optionsWrapperRef?: React.RefObject<HTMLDivElement> = createRef();

  recalculateFixedOptionsPosition = (position?: 'top' | 'bottom') => {
    const { optionsPosition } = this.props;

    const style = this.optionsWrapperRef?.current?.style;
    const boundingRect = this.selectRef?.current?.getBoundingClientRect?.();

    style?.setProperty('width', `${boundingRect?.width}px`);
    style?.setProperty('left', `${boundingRect?.left}px`);
    if ((position || optionsPosition) === 'bottom') {
      style?.setProperty('top', `${(boundingRect?.bottom || 0) + 3}px`);
      style?.removeProperty('bottom');
    } else {
      style?.setProperty(
        'bottom',
        `${window.innerHeight - (boundingRect?.top || 0)!}px`
      );
      style?.removeProperty('top');
    }
  };

  recalculateOptionsPosition = () => {
    const { onOptionsPosition, optionsHeight, optionsFixed, size } = this.props;
    const { options: filteredOptions } = this.state;
    const typeaheadHeight = getTypeaheadHeight(this.typeaheadEnabled(), size);

    const { position } = getDropdownOptionsSettings({
      element: this.selectRef?.current,
      options: filteredOptions,
      optionsHeight,
      maxOptionsHeight: MAX_OPTIONS_SIZE,
      extraContentHeight: typeaheadHeight,
    });

    if (optionsFixed) {
      this.recalculateFixedOptionsPosition();
    }

    onOptionsPosition(position);
  };

  optionSelected(
    e: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
    optionItem: DropdownOption
  ) {
    const { onSelect, keepOpenOnSelect } = this.props;
    e.preventDefault();
    e.stopPropagation();

    const optionItemSlug = optionItem.slug;

    if (onSelect) {
      onSelect(optionItemSlug);
    }

    if (optionItem.fn) {
      optionItem.fn(e);
    }

    if (keepOpenOnSelect) {
      this.setState({ selectedHighlighted: null });
    } else {
      this.setState(
        { selectedHighlighted: null, activeMenu: false, focused: false },
        () => {
          this.handleTypeaheadChange('');
        }
      );
    }
  }

  optionMenuItems() {
    const { active } = this.props;
    const { selectedHighlighted } = this.state;
    const options = this.selectOptions();
    const links = options.map((optionItem) => {
      const item = { ...optionItem };
      item.fn = (event) => this.optionSelected(event, optionItem);
      return item;
    });

    return {
      type: 'verticalbox',
      selected: selectedHighlighted,
      active,
      links,
    };
  }

  toggleOption(e: React.MouseEvent<HTMLElement>) {
    const { activeMenu } = this.state;
    e.preventDefault();
    e.stopPropagation();

    this.recalculateOptionsPosition();

    this.setState({ activeMenu: !activeMenu });
  }

  focusOption(e: React.FocusEvent<HTMLElement>, focused: boolean) {
    const { onFocus } = this.props;

    if (e.type === 'focus' || e.nativeEvent.relatedTarget) {
      this.setState({ focused });
    }

    if (onFocus) {
      onFocus(focused);
    }
  }

  typeaheadEnabled() {
    const { options: unfilteredOptions } = this.props;
    return unfilteredOptions && unfilteredOptions.length > 6;
  }

  selectOptions() {
    const { options: filteredOptions, typeaheadValue } = this.state;
    const { options: unfilteredOptions } = this.props;
    return this.typeaheadEnabled() && typeaheadValue
      ? filteredOptions
      : unfilteredOptions;
  }

  renderTypeahead() {
    const { size, intl, typeaheadPlaceholder } = this.props;

    const { typeaheadValue } = this.state;

    const placeholder = typeaheadPlaceholder || i18n.typeahead;

    return (
      <styled.OptionsTypeahead
        size={size}
        placeholder={intl.formatMessage(placeholder as MessageDescriptor)}
        onInnerRef={(input: HTMLInputElement | null) => {
          this.typeaheadInput = input;
        }}
        value={typeaheadValue}
        shouldValidate={false}
        onChange={(value: string) => this.handleTypeaheadChange(value)}
        onKeyUp={(e: React.KeyboardEvent<HTMLElement>) => this.handleKeyUp(e)}
        onKeyPress={(e: React.KeyboardEvent<HTMLElement>) =>
          this.handleKeyPress(e)
        }
        onFocus={(e: React.FocusEvent<HTMLElement>) =>
          this.focusOption(e, true)
        }
        onBlur={(e: React.FocusEvent<HTMLElement>) =>
          this.focusOption(e, false)
        }
      />
    );
  }

  renderOptionsCount(shownCount: number) {
    const { optionsCount, options: unfilteredOptions } = this.props;

    if (!optionsCount) return null;

    return (
      <styled.OptionsCount>
        <FormattedMessage
          {...optionsCount}
          values={{ shown: shownCount, total: unfilteredOptions.length }}
        />
      </styled.OptionsCount>
    );
  }

  render() {
    const {
      className,
      padding = null,
      size = 'default',
      children,
      selectTag,
      disabled,
      optionsPosition = 'bottom',
      displayClear,
      dataManual,
      options: unfilteredOptions,
      optionsFixed,
    } = this.props;

    const { activeMenu: stateActiveMenu, focused, hidden } = this.state;
    const options = this.selectOptions();
    const activeMenu = !disabled && (stateActiveMenu || focused);
    const iconDirection = activeMenu ? 'CaretUp' : 'CaretDown';
    const SelectedTag = (selectTag === 'button'
      ? styled.OptionSelectedButton
      : styled.OptionSelectedLink) as React.FC<SelectedTagProps>;

    return (
      <ReactPageClick
        notify={() =>
          activeMenu &&
          this.setState(
            { activeMenu: false, focused: false, hidden: true },
            () => {
              this.setState({ options: unfilteredOptions });
              this.handleTypeaheadChange('');
            }
          )
        }
      >
        <styled.Select
          className={className}
          ref={this.selectRef as React.RefObject<HTMLDivElement>}
          data-manual={dataManual}
        >
          {displayClear ? (
            <styled.ClearSelection
              onClick={this.handleClearSelection}
              type="blank"
            >
              <styled.Icon icon="X" />
              <styled.Divider />
            </styled.ClearSelection>
          ) : null}
          <SelectedTag
            onClick={(e: React.MouseEvent<HTMLElement>) => this.toggleOption(e)}
            onFocus={(e: React.FocusEvent<HTMLElement>) =>
              this.focusOption(e, true)
            }
            onBlur={(e: React.FocusEvent<HTMLElement>) =>
              this.focusOption(e, false)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLElement>) =>
              this.handleKeyDown(e)
            }
            onKeyUp={(e: React.KeyboardEvent<HTMLElement>) =>
              this.handleKeyUp(e)
            }
            padding={padding}
          >
            {children}
            <styled.Icon
              icon={iconDirection}
              marginLeft={displayClear && '3.4rem'}
            />
          </SelectedTag>
          <styled.OptionsWrapper
            ref={this.optionsWrapperRef as React.RefObject<HTMLDivElement>}
            activeMenu={activeMenu}
            optionsFixed={optionsFixed}
            size={size}
            hidden={hidden}
          >
            {activeMenu && (
              <>
                {optionsPosition === 'bottom' &&
                  this.typeaheadEnabled() &&
                  this.renderTypeahead()}
                {!isEmpty(options) ? (
                  <styled.Options
                    context={this.optionMenuItems()}
                    dropdownSize={24}
                    maxItems={5}
                    ref={(optionsItems) => {
                      this.optionsMenu = optionsItems;
                    }}
                  />
                ) : (
                  <styled.EmptyState>
                    <FormattedMessage {...i18n.noRecords} />
                  </styled.EmptyState>
                )}
                {this.typeaheadEnabled() &&
                  this.renderOptionsCount(options.length)}
                {optionsPosition === 'top' &&
                  this.typeaheadEnabled() &&
                  this.renderTypeahead()}
              </>
            )}
          </styled.OptionsWrapper>
        </styled.Select>
      </ReactPageClick>
    );
  }
}

export const Dropdown = injectIntl(DropdownComponent);
